import { Modal } from 'antd'
import styled from 'styled-components'
import { Building2, Compass, Lightbulb, Layers, FileText } from 'lucide-react'
import { SPACING, TYPOGRAPHY, BORDER_RADIUS, FONT_WEIGHT } from '../../../styles/theme'
import { CATEGORY_STYLES } from '../../../constants/mindmapConfig'

interface DescendantInfo {
  id: string
  label: string
  category: string
}

interface DeleteConfirmationModalProps {
  isVisible: boolean
  nodeLabel: string
  descendants: DescendantInfo[]
  onConfirm: () => void
  onCancel: () => void
}

const WarningText = styled.p`
  color: var(--color-text-primary);
  font-size: ${TYPOGRAPHY.body.fontSize};
  margin: 0 0 ${SPACING.lg} 0;
  line-height: 1.5;
`

const NodeName = styled.span`
  font-weight: ${FONT_WEIGHT.semibold};
`

const DescendantListContainer = styled.div`
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.md};
  background: var(--color-bg-secondary);
`

const DescendantItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md};
  padding: ${SPACING.sm} ${SPACING.md};
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    border-bottom: none;
  }
`

const DescendantLabel = styled.span`
  color: var(--color-text-primary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`

const getCategoryColor = (category: string): string => {
  if (category in CATEGORY_STYLES) {
    const styles = CATEGORY_STYLES[category as keyof typeof CATEGORY_STYLES]
    return styles.iconColor
  }
  return '#6b7280'
}

const CategoryCircle = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background: var(--color-node-bg);
  border: 2px solid ${(props) => props.$color};
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.05));
`

const CountText = styled.p`
  color: var(--color-text-secondary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  margin: 0 0 ${SPACING.sm} 0;
`

const getCategoryIcon = (category: string, color: string) => {
  const iconProps = { size: 12, color, strokeWidth: 2.5 }
  switch (category) {
    case 'venture':
      return <Building2 {...iconProps} />
    case 'need':
      return <Compass {...iconProps} />
    case 'idea':
      return <Lightbulb {...iconProps} />
    case 'summarized':
      return <Layers {...iconProps} />
    default:
      return <FileText {...iconProps} />
  }
}

export function DeleteConfirmationModal({
  isVisible,
  nodeLabel,
  descendants,
  onConfirm,
  onCancel,
}: DeleteConfirmationModalProps) {
  return (
    <Modal
      title="Delete Node"
      open={isVisible}
      onOk={onConfirm}
      onCancel={onCancel}
      okText="Delete All"
      okButtonProps={{ danger: true }}
      cancelText="Cancel"
      width={400}
      destroyOnHidden={true}
    >
      <WarningText>
        Deleting <NodeName>"{nodeLabel}"</NodeName> will also delete {descendants.length} child node
        {descendants.length !== 1 ? 's' : ''}.
      </WarningText>

      <CountText>Nodes to be deleted:</CountText>

      <DescendantListContainer>
        {descendants.map((item) => {
          const color = getCategoryColor(item.category)
          return (
            <DescendantItem key={item.id}>
              <CategoryCircle $color={color}>
                {getCategoryIcon(item.category, color)}
              </CategoryCircle>
              <DescendantLabel>{item.label || 'Untitled'}</DescendantLabel>
            </DescendantItem>
          )
        })}
      </DescendantListContainer>
    </Modal>
  )
}
